<template>
	<div class="tab-pane h-100" :class="{ active: defaultTab, show: defaultTab }" :id="id">
		<div class="flex-v h-100">
			<div class="p-4 _600 nav-title flex-fixed">User Assignment</div>
			<div class="flex-grow flex-v">
				<dual-table
					leftLabel="Assign Users"
					leftSearch="Search assigned users..."
					:leftItems="hasProvider"
					:leftFields="hasFields"
					:leftControl="hasControl"
					rightLabel="Add"
					rightSearch="Search available users..."
					:rightItems="canProvider"
					:rightFields="canFields"
					:rightControl="canControl"
				>
					<!-- Left table slots -->
					<!-- Name Virtual Column -->
					<template slot="left:name" slot-scope="data"
						>{{ data.item.first_name }} {{ data.item.last_name }}</template
					>

					<!-- Leader -->
					<template slot="left:leader" slot-scope="data">
						<nobr>
							<div class="mx-auto w30">
								<button
									v-if="!isTeamLeader(data.item)"
									class="btn btn-sm btn-icon btn-rounded m-0"
									@click="attemptPromoteUser(data.item)"
									v-tippy
									title="Promote to Team Leader"
								>
									<i class="far fa-angle-up"></i>
								</button>
								<button
									v-if="isTeamLeader(data.item)"
									class="btn btn-sm btn-icon btn-rounded green text-white m-0"
									@click="attemptDemoteUser(data.item)"
									v-tippy
									title="Demote from Team Leader"
								>
									<i class="far fa-check"></i>
								</button>
							</div>
						</nobr>
					</template>

					<!-- Actions -->
					<template slot="left:actions" slot-scope="data">
						<nobr>
							<div class="mx-auto w30">
								<button
									v-if="!data.item.inProgress"
									class="btn btn-sm btn-icon btn-rounded red text-white m-0"
									@click="attemptRemoveUser(data.item)"
									v-tippy
									title="Remove from Team"
								>
									<i class="far fa-minus"></i>
								</button>
								<button
									v-if="data.item.inProgress"
									class="btn btn-sm btn-icon btn-rounded m-0"
									v-tippy
									title="Removing..."
								>
									<loading type="icon" />
								</button>
							</div>
						</nobr>
					</template>

					<!-- Right table slots -->
					<!-- Name Virtual Column -->
					<template slot="right:name" slot-scope="data"
						>{{ data.item.first_name }} {{ data.item.last_name }}</template
					>

					<!-- Name Virtual Column -->
					<template slot="right:team_name" slot-scope="data">
						<span v-if="!data.item.team_name" class="text-extra-muted">None</span>
						<span v-if="data.item.team_name">{{ data.item.team_name }}</span>
					</template>

					<!-- Actions -->
					<template slot="right:actions" slot-scope="data">
						<nobr>
							<div class="mx-auto w30">
								<button
									v-if="!data.item.inProgress"
									class="btn btn-sm btn-icon btn-rounded success text-white m-0"
									@click="attemptAddUser(data.item)"
									v-tippy
									title="Assign to Team"
								>
									<i class="far fa-plus"></i>
								</button>
								<button
									v-if="data.item.inProgress"
									class="btn btn-sm btn-icon btn-rounded m-0"
									v-tippy
									title="Adding..."
								>
									<loading type="icon" />
								</button>
							</div>
						</nobr>
					</template>
				</dual-table>
			</div>
		</div>

		<save-required-modal
			:dirty="dirty"
			:valid="valid"
			:save="save"
			:next="saveReqNext"
			objectText="Team"
			:objectName="team.name"
			:actionText="saveReqAction"
			v-model="saveReqModal"
		/>
	</div>
</template>

<style scoped>
.w90 {
	width: 90px;
}
.w30 {
	width: 30px;
}
.loader {
	font-size: 128px;
	line-height: 128px;
	color: gray;
	background-color: lightgray;
	text-align: center;
}
.overlay {
	position: absolute;
	top: 0px;
	bottom: 0px;
	left: 0px;
	right: 0px;
}
</style>

<script>
import TeamService from "@/services/TeamService";
import notie from "@/services/NotieService";

import DualTable from "@/components/DualTable";
import SaveRequiredModal from "@/components/SaveRequiredModal";

export default {
	name: "TeamEditUsers",

	props: ["id", "team", "dirty", "valid", "save", "defaultTab"],

	components: {
		"dual-table": DualTable,
		"save-required-modal": SaveRequiredModal
	},

	data() {
		return {
			hasFields: [
				{
					key: "name",
					label: this.$i18n.t("label.name"),
					sortable: true
				},
				{
					key: "email",
					label: "Email",
					sortable: true
				},
				{
					key: "leader",
					label: "Leader",
					thStyle: { "text-align": "center" },
					tdClass: "p-custom py-0 v-mid"
				},
				{
					key: "actions",
					label: this.$i18n.t("label.actions"),
					thStyle: { "text-align": "center" },
					tdClass: "p-custom py-0 v-mid"
				}
			],
			hasControl: {
				totalRows: 0,
				currentPage: 1,
				perPage: 10,
				filter: "",
				sortBy: "",
				sortDesc: false,
				emptyText: ""
			},
			canFields: [
				{
					key: "name",
					label: this.$i18n.t("label.name"),
					sortable: true
				},
				{
					key: "email",
					label: "Email",
					sortable: true
				},
				{
					key: "team_name",
					label: "Current Team"
				},
				{
					key: "actions",
					label: this.$i18n.t("label.actions"),
					thStyle: { "text-align": "center" },
					tdClass: "p-custom py-0 v-mid"
				}
			],
			canControl: {
				totalRows: 0,
				currentPage: 1,
				perPage: 10,
				filter: "",
				sortBy: "",
				sortDesc: false,
				emptyText: ""
			},

			tempLeaderOverride: {},

			saveReqModal: false,
			saveReqAction: "continue",
			saveReqNext: () => {}
		};
	},

	created() {},

	methods: {
		hasProvider(ctx) {
			if (this.team.id) {
				this.hasControl.emptyText = "There are no records to show";
				return TeamService.getTeamUsers(this.team.id, ctx)
					.then(resp => {
						this.hasControl.totalRows = resp.data.totalRows;
						return resp.data.users;
					})
					.catch(err => {
						notie.error("Failed load assignable users", err);
						return [];
					});
			} else {
				this.hasControl.emptyText = "Cannot assign users to a team before saving";
				return [];
			}
		},

		canProvider(ctx) {
			ctx.inverse = "t";
			if (this.team.id) {
				this.canControl.emptyText = "There are no records to show";
				return TeamService.getTeamUsers(this.team.id, ctx)
					.then(resp => {
						this.canControl.totalRows = resp.data.totalRows;
						return resp.data.users;
					})
					.catch(err => {
						notie.error("Failed to get team's users", err);
						return [];
					});
			} else {
				this.canControl.emptyText = "Cannot assign users to a team before saving";
				return [];
			}
		},

		refreshTables() {
			this.$root.$emit("bv::refresh::table", "leftTable");
			this.$root.$emit("bv::refresh::table", "rightTable");
		},

		attemptAddUser(user) {
			if (this.dirty) {
				this.saveReqNext = () => {
					this.addUser(user);
				};
				this.saveReqAction = "add an user to the team";
				this.saveReqModal = true;
			} else {
				this.addUser(user);
			}
		},

		addUser(user) {
			user.inProgress = true;
			TeamService.assignUserToTeam(user.id, this.team.id)
				.then(resp => {
					notie.info("User added");
					this.refreshTables();
				})
				.catch(err => {
					notie.error("Failed to add user", err);
				})
				.finally(() => {
					user.inProgress = false;
				});
		},

		attemptRemoveUser(user) {
			if (this.dirty) {
				this.saveReqNext = () => {
					this.removeUser(user);
				};
				this.saveReqAction = "remove an user from the team";
				this.saveReqModal = true;
			} else {
				this.removeUser(user);
			}
		},

		removeUser(user) {
			user.inProgress = true;
			TeamService.removeUserTeam(user.id)
				.then(resp => {
					notie.info("User removed");
					this.refreshTables();
				})
				.catch(err => {
					notie.error("Failed to remove user", err);
				})
				.finally(() => {
					user.inProgress = false;
				});
		},

		attemptPromoteUser(user) {
			if (this.dirty) {
				this.saveReqNext = () => {
					this.addUser(user);
				};
				this.saveReqAction = "change team leaders";
				this.saveReqModal = true;
			} else {
				this.promoteUser(user);
			}
		},

		promoteUser(user) {
			TeamService.promoteUser(this.team.id, user.id)
				.then(resp => {
					notie.info("User promoted");
					this.tempLeaderOverride[user.id] = true;
					this.refreshTables();
				})
				.catch(err => {
					notie.error("Failed to promote user", err);
				});
		},

		attemptDemoteUser(user) {
			if (this.dirty) {
				this.saveReqNext = () => {
					this.addUser(user);
				};
				this.saveReqAction = "change team leaders";
				this.saveReqModal = true;
			} else {
				this.demoteUser(user);
			}
		},

		demoteUser(user) {
			TeamService.demoteUser(this.team.id, user.id)
				.then(resp => {
					notie.info("User demoted");
					this.tempLeaderOverride[user.id] = false;
					this.refreshTables();
				})
				.catch(err => {
					notie.error("Failed to demote user", err);
				});
		},

		sortTables() {
			this.availUsers = _.orderBy(this.availUsers, ["first_name"], ["asc"]);
			this.team.users = _.orderBy(this.team.users, ["first_name"], ["asc"]);
		},

		click(id) {
			var el = document.getElementById(id);
			if (el) {
				el.click();
			}
		},

		isTeamLeader(user) {
			if (!this.team && this.team.leader_ids) {
				return false;
			}

			if (this.tempLeaderOverride[user.id] != undefined) {
				return this.tempLeaderOverride[user.id];
			}

			return this.team.leader_ids.includes(user.id);
		}
	}
};
</script>



// WEBPACK FOOTER //
// src/components/team/TeamEditUsers.vue